import { Link as MUILink, LinkProps as MUILinkProps, SxProps } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { t } from 'i18next';
import { forwardRef } from 'react';
import { Link as ReactLink, LinkProps as ReactLinkProps } from 'react-router-dom';

// Extract the SVG content of the MUI `OpensInNew` icon to render in the ::after selector of an external link
const OPENS_IN_NEW_CONTENT =
  '<svg xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3z"></path></svg>';

const iconStyle: SxProps = {
  '&::after': {
    // The icon is rendered as a ::after element which must have a empty (but set) content:
    // `maskImage` and `backgroundColor` render the icon with the actual `currentColor`, which
    // is not known by the SVG source and would be black otherwise.
    content: '""',
    maskImage: `url('data:image/svg+xml,${OPENS_IN_NEW_CONTENT}')`,
    backgroundColor: 'currentColor',
    display: 'inline-flex',
    width: '1em',
    height: '1em',
    transform: 'translate(0.15em, 0.15em)',
  },
  // Override icon position to account for inner `MenuItem` alignment
  '&.MuiMenuItem-root::after': { transform: 'translate(0.15em, 0)', ml: '0.25em', opacity: 0.75 },
  // Do not show the icon for `IconButton`
  '&.MuiIconButton-root::after': { display: 'none' },
  // Override icon position for outlined buttons
  '&.MuiButton-outlined::after': { transform: 'translate(0.15em, 0)', ml: '0.25em' },
  // Override icon position to account for padded `Chip` inner label
  '&.MuiChip-root::after': { ml: -1, mr: 1, transform: 'none' },
  // Override `TypographyOverflow` styles to place the icon on the same line
  '&>.typography-overflow': { maxWidth: 'calc(100% - 1em)', float: 'left', '&:hover': { textDecoration: 'underline' } },
};

export const A11yLink = forwardRef<HTMLAnchorElement, Omit<ReactLinkProps, 'to'> & MUILinkProps>((props, ref) => {
  const { href, children, target, 'aria-label': ariaLabel, sx = {}, ...other } = props;

  const computedAriaLabel = ariaLabel ?? children;
  const a11yProps =
    target === '_blank'
      ? { sx: deepmerge(sx, iconStyle), 'aria-label': t('a11y.general.prefixOpensInNewTab', { prefix: computedAriaLabel }) }
      : { sx, 'aria-label': ariaLabel };

  if (href && !(href.startsWith('/') || href.startsWith('http'))) {
    console.error(`Unexpected relative URL in A11YLink: ${href}`);
  }

  return (
    <MUILink component={ReactLink} ref={ref} to={href} target={target} {...other} {...a11yProps}>
      {children}
    </MUILink>
  );
});
